// src/api.js
const API_BASE_URL = "https://te-ayudo.es/back/api";
const MAIL_API = "mail";


/**
 * Convierte un objeto de parámetros en una cadena de consulta.
 *
 * Admite:
 * - strings
 * - números
 * - booleanos
 * - arrays
 *
 * Ejemplo:
 * {
 *   q: "María",
 *   specialty_codes: ["psicologia", "logopedia"]
 * }
 *
 * Resultado:
 * ?q=Mar%C3%ADa&specialty_codes=psicologia%2Clogopedia
 */
function buildQueryString(params = {}) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return;
    }

    if (Array.isArray(value)) {
      const cleanValues = value
        .map((item) => String(item).trim())
        .filter(Boolean);

      if (cleanValues.length > 0) {
        searchParams.set(key, cleanValues.join(","));
      }

      return;
    }

    if (typeof value === "boolean") {
      searchParams.set(key, value ? "1" : "0");
      return;
    }

    searchParams.set(key, String(value));
  });

  const query = searchParams.toString();

  return query ? `?${query}` : "";
}

/**
 * Intenta recuperar el JSON de una respuesta.
 *
 * También maneja el caso en el que PHP devuelve:
 * - un warning;
 * - un fatal error;
 * - HTML;
 * - una respuesta vacía.
 */
async function parseResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  const rawText = await response.text();

  if (!rawText.trim()) {
    if (response.ok) {
      return {
        ok: true,
      };
    }

    throw new Error(
      `El servidor respondió con el código ${response.status}.`
    );
  }

  let data = null;

  if (contentType.includes("application/json")) {
    try {
      data = JSON.parse(rawText);
    } catch {
      throw new Error(
        "El servidor devolvió una respuesta JSON no válida."
      );
    }
  } else {
    try {
      data = JSON.parse(rawText);
    } catch {
      const cleanText = rawText
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      throw new Error(
        cleanText ||
          `El servidor respondió con el código ${response.status}.`
      );
    }
  }

  if (!response.ok || data?.ok === false) {
    const error = new Error(
      data?.error ||
        data?.message ||
        `El servidor respondió con el código ${response.status}.`
    );

    error.status = response.status;
    error.data = data;

    throw error;
  }

  return data;
}

/**
 * Petición genérica contra la API.
 */
async function apiRequest(endpoint, options = {}) {
  const {
    method = "GET",
    body,
    headers = {},
    signal,
  } = options;

  const requestOptions = {
    method,
    credentials: "include",
    signal,
    headers: {
      Accept: "application/json",
      ...headers,
    },
  };

  if (body !== undefined && body !== null) {
    if (body instanceof FormData) {
      requestOptions.body = body;
    } else {
      requestOptions.headers["Content-Type"] =
        "application/json";

      requestOptions.body = JSON.stringify(body);
    }
  }

  let response;

  try {
    response = await fetch(
      `${API_BASE_URL}/${endpoint.replace(/^\/+/, "")}`,
      requestOptions
    );
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }

    throw new Error(
      "No se ha podido conectar con el servidor. Comprueba tu conexión e inténtalo de nuevo."
    );
  }

  return parseResponse(response);
}

/**
 * Carga los pacientes que pueden utilizarse como destinatarios.
 *
 * @param {Object} filters
 * @param {string} filters.q
 * @param {string[]} filters.specialtyCodes
 * @param {number[]} filters.specialtyIds
 * @param {boolean} filters.onlyWithEmail
 * @param {AbortSignal} filters.signal
 */
export async function getCampaignContacts({
  q = "",
  specialtyCodes = [],
  specialtyIds = [],
  onlyWithEmail = true,
  signal,
} = {}) {
  const query = buildQueryString({
    q,
    specialty_codes: specialtyCodes,
    specialty_ids: specialtyIds,
    only_with_email: onlyWithEmail,
  });

 return apiRequest(`${MAIL_API}/campaign.contacts.php${query}`, {
  signal,
});
}

/**
 * Solicita al servidor el HTML definitivo de la campaña.
 *
 * Este método quedará operativo cuando creemos:
 *
 * campaign.preview.php
 */
export async function previewCampaign({
  subject = "",
  body = "",
  signal,
} = {}) {
return apiRequest(`${MAIL_API}/campaign.preview.php`, {
  method: "POST",
  signal,
  body: {
    subject,
    body,
  },
});
}

/**
 * Envía una campaña sin archivos adjuntos.
 *
 * Este método quedará operativo cuando creemos:
 *
 * campaign.send.php
 */
export async function sendCampaign(campaign, { signal } = {}) {
  if (!campaign || typeof campaign !== "object") {
    throw new Error(
      "No se han recibido los datos de la campaña."
    );
  }

return apiRequest(`${MAIL_API}/campaign.send.php`, {
  method: "POST",
  signal,
  body: {
    subject: campaign.subject || "",
    body: campaign.body || "",
    recipient_mode: campaign.recipientMode || "all",
    recipients: Array.isArray(campaign.recipients)
      ? campaign.recipients
      : [],
    send_copy: campaign.sendCopy !== false,
  },
});
}

/**
 * Envía una campaña con archivos adjuntos.
 *
 * El cuerpo de la campaña se incorpora como JSON dentro
 * del campo "campaign".
 *
 * Los archivos se añaden con el nombre "attachments[]".
 */
export async function sendCampaignWithAttachments(
  campaign,
  { signal } = {}
) {
  if (!campaign || typeof campaign !== "object") {
    throw new Error(
      "No se han recibido los datos de la campaña."
    );
  }

  const formData = new FormData();

  formData.append(
    "campaign",
    JSON.stringify({
      subject: campaign.subject || "",
      body: campaign.body || "",
      recipient_mode:
        campaign.recipientMode || "all",
      recipients: Array.isArray(campaign.recipients)
        ? campaign.recipients
        : [],
      send_copy: campaign.sendCopy !== false,
    })
  );

  const attachments = Array.isArray(
    campaign.attachments
  )
    ? campaign.attachments
    : [];

  attachments.forEach((attachment) => {
    const file =
      attachment instanceof File
        ? attachment
        : attachment?.file;

    if (file instanceof File) {
      formData.append("attachments[]", file);
    }
  });

 return apiRequest(`${MAIL_API}/campaign.send.php`, {
  method: "POST",
  signal,
  body: formData,
});
}

/**
 * Obtiene el historial de campañas.
 *
 * Este método quedará operativo cuando creemos:
 *
 * campaign.history.php
 */
export async function getCampaignHistory({
  q = "",
  page = 1,
  pageSize = 20,
  status = "",
  dateFrom = "",
  dateTo = "",
  signal,
} = {}) {
  const query = buildQueryString({
    q,
    page,
    page_size: pageSize,
    status,
    date_from: dateFrom,
    date_to: dateTo,
  });

return apiRequest(`${MAIL_API}/campaign.history.php${query}`, {
  signal,
});
}

/**
 * Recupera una campaña concreta con sus destinatarios.
 *
 * Este método quedará operativo cuando campaign.history.php
 * admita el parámetro id.
 */
export async function getCampaignDetail(
  campaignId,
  { signal } = {}
) {
  const id = Number(campaignId);

  if (!Number.isInteger(id) || id <= 0) {
    throw new Error(
      "El identificador de la campaña no es válido."
    );
  }

  const query = buildQueryString({
    id,
  });

return apiRequest(`${MAIL_API}/campaign.history.php${query}`, {
  signal,
});
}

/**
 * Obtiene las plantillas guardadas.
 *
 * Este método quedará operativo cuando creemos:
 *
 * campaign.templates.php
 */
export async function getCampaignTemplates({
  q = "",
  specialtyCode = "",
  signal,
} = {}) {
  const query = buildQueryString({
    q,
    specialty_code: specialtyCode,
  });

return apiRequest(`${MAIL_API}/campaign.templates.php${query}`, {
  signal,
});
}

/**
 * Crea una plantilla.
 */
export async function createCampaignTemplate(
  template,
  { signal } = {}
) {
return apiRequest(`${MAIL_API}/campaign.templates.php`, {
  method: "POST",
  signal,
  body: {
    action: "create",
    name: template?.name || "",
    subject: template?.subject || "",
    body: template?.body || "",
    specialty_code: template?.specialtyCode || null,
    is_favorite: template?.isFavorite === true,
  },
});
}

/**
 * Actualiza una plantilla.
 */
export async function updateCampaignTemplate(
  templateId,
  template,
  { signal } = {}
) {
  const id = Number(templateId);

  if (!Number.isInteger(id) || id <= 0) {
    throw new Error(
      "El identificador de la plantilla no es válido."
    );
  }

return apiRequest(`${MAIL_API}/campaign.templates.php`, {
  method: "POST",
  signal,
  body: {
    action: "update",
    id,
    name: template?.name || "",
    subject: template?.subject || "",
    body: template?.body || "",
    specialty_code: template?.specialtyCode || null,
    is_favorite: template?.isFavorite === true,
  },
});
}

/**
 * Elimina una plantilla.
 */
export async function deleteCampaignTemplate(
  templateId,
  { signal } = {}
) {
  const id = Number(templateId);

  if (!Number.isInteger(id) || id <= 0) {
    throw new Error(
      "El identificador de la plantilla no es válido."
    );
  }
return apiRequest(`${MAIL_API}/campaign.templates.php`, {
  method: "POST",
  signal,
  body: {
    action: "delete",
    id,
  },
});
}

export default {
  getCampaignContacts,
  previewCampaign,
  sendCampaign,
  sendCampaignWithAttachments,
  getCampaignHistory,
  getCampaignDetail,
  getCampaignTemplates,
  createCampaignTemplate,
  updateCampaignTemplate,
  deleteCampaignTemplate,
};